import type { RuntimeConfig } from '../config';
import { IdentityError, type IdentityService } from './IdentityService';

const requestTimeoutMs = 8_000;

export type FriendPresence = 'offline' | 'station' | 'crew' | 'queue' | 'match';

export interface FriendContact {
  userId: string;
  displayName: string;
  state: 'friend' | 'incoming' | 'outgoing';
  online: boolean;
  presence?: FriendPresence;
  unreadCount: number;
}

export interface SocialOverview {
  friends: FriendContact[];
  incoming: FriendContact[];
  outgoing: FriendContact[];
}

export interface DirectMessage {
  id: number;
  senderId: string;
  recipientId: string;
  body: string;
  createdAt: string;
}

export class SocialService {
  public constructor(private readonly config: RuntimeConfig, private readonly identity: IdentityService) {}

  public overview(): Promise<SocialOverview> {
    return this.request('/api/v1/social');
  }

  public requestFriend(callTag: string): Promise<FriendContact> {
    return this.request('/api/v1/friends/requests', { method: 'POST', body: JSON.stringify({ callTag }) });
  }

  public accept(userId: string): Promise<void> {
    return this.request('/api/v1/friends/' + encodeURIComponent(userId) + '/accept', { method: 'POST' });
  }

  public remove(userId: string): Promise<void> {
    return this.request('/api/v1/friends/' + encodeURIComponent(userId), { method: 'DELETE' });
  }

  public messages(userId: string, after = 0): Promise<DirectMessage[]> {
    const cursor = after > 0 ? '?after=' + String(after) : '';
    return this.request('/api/v1/friends/' + encodeURIComponent(userId) + '/messages' + cursor);
  }

  public send(userId: string, body: string): Promise<DirectMessage> {
    return this.request('/api/v1/friends/' + encodeURIComponent(userId) + '/messages', {
      method: 'POST', body: JSON.stringify({ body }),
    });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const authorization = this.identity.authorization();
    if (!authorization) throw new IdentityError('Log in to use the crew network.', 'account_required');
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      const response = await fetch(this.config.apiOrigin + path, {
        ...init,
        headers: { 'Content-Type': 'application/json', Authorization: authorization, ...init.headers },
        signal: controller.signal,
      });
      this.requireCurrentAuthorization(authorization);
      if (response.status === 204) return undefined as T;
      const payload = await response.json() as T & { error?: { code?: string; message?: string } };
      this.requireCurrentAuthorization(authorization);
      if (!response.ok) {
        throw new IdentityError(payload.error?.message ?? 'The crew network failed.', payload.error?.code ?? 'request_failed');
      }
      return payload;
    } catch (error) {
      if (error instanceof IdentityError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new IdentityError('The crew network did not answer in time.', 'timeout');
      }
      throw new IdentityError('Cannot reach the crew network.', 'offline');
    } finally {
      window.clearTimeout(timer);
    }
  }

  private requireCurrentAuthorization(authorization: string): void {
    if (this.identity.authorization() !== authorization) {
      throw new IdentityError('The account session changed. Retry this crew signal.', 'session_changed');
    }
  }
}
